import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../Redux/Store';
import { ActiveDiningTable } from '../Redux/ActiveStore/activeStoreSlice';
import { useAudioPermission } from './useAudioPermission';

interface UseNotificationSSEProps {
  url: string;
  storeId: string;
  clerkId: string | undefined;
}

// ベル・音を鳴らさず、稼働セッション再取得のきっかけとしてだけ転送するサイレント通知種別（ADR 0068）。
// checkoutAcknowledged は店員自身の受領・取消操作の同期通知なので、履歴にも残さない。
const SILENT_NOTIFICATION_TYPES = new Set(['checkoutAcknowledged']);

export const useOrderNotificationSSE = ({ url, storeId, clerkId }: UseNotificationSSEProps) => {
  const { audioAllowed, unlockAudio, lockAudio, playNotificationSound, startSSE } = useAudioPermission();
  const [notificationShake, setNotificationShake] = useState<boolean>(false);
  const [notificationMessages, setNotificationMessages] = useState<{ time: string, message: string }[]>([]);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationColor, setNotificationColor] = useState<'inherit' | 'blue'>('inherit');
  const [orderNotificationData, setOrderNotificationData] = useState<{ storeId: string, activeStoreId: string, activeDiningTableId: string, notificationType: string, activeOrderId: string }[]>([]);
  const sseRef = useRef<EventSource | null>(null);

  const activeStore = useSelector((state: RootState) => state.activeStore.store);
  const { store_id } = useParams<{ store_id: string }>();
  // const storeId = store_id;
  // const clerkId = localStorage.getItem('clerk_id');

  useEffect(() => {
    if (!url || !storeId || !clerkId) {
      return;
    }

    try {
      const es = startSSE(url);
      sseRef.current = es;

      const handleMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.storeId && data.activeStoreId && data.activeDiningTableId && data.notificationType) {
            const diningTable = activeStore?.activeTables.find((table: ActiveDiningTable) => table.id === data.activeDiningTableId);
            const diningTableName = diningTable ? diningTable.diningTable.name : 'Unknown Table';
            const currentTime = new Date().toLocaleTimeString();
            const isSilent = SILENT_NOTIFICATION_TYPES.has(data.notificationType);
            
            if (!isSilent) {
              setNotificationShake(true);
            }
            // ActiveInOrderのidをActiveOrderIdとして追加
            // サイレント種別で activeOrderId が無い場合は、同一テーブルの 2 回目以降が重複排除で捨てられて再取得が走らなくなるため、受信ごとに一意なキーを振る
            const dataWithActiveOrderId = {
              ...data,
              activeOrderId:
                data.activeOrderId ||
                (isSilent ? `${data.notificationType}-${Date.now()}-${Math.random().toString(36).slice(2)}` : data.activeStoreId) // activeOrderIdがない場合はactiveStoreIdを使用
            };
            
            // 通知データを配列に追加（重複チェック付き - ActiveOrderIdを含む）
            setOrderNotificationData(prevData => {
              // ActiveOrderIdを含めた一意の識別子を生成
              const notificationKey = `${data.storeId}-${data.activeStoreId}-${data.activeDiningTableId}-${data.notificationType}-${dataWithActiveOrderId.activeOrderId}`;
              
              // ActiveOrderIdを含めて重複チェック
              const isExisting = prevData.some(
                item => {
                  const itemKey = `${item.storeId}-${item.activeStoreId}-${item.activeDiningTableId}-${item.notificationType}-${item.activeOrderId}`;
                  return itemKey === notificationKey;
                }
              );
              
              // 重複する通知の場合は、既存の配列を返す
              if (isExisting) {
                console.log('Notification with same ActiveOrderId already exists in useOrderNotificationSSE, not adding:', dataWithActiveOrderId);
                return prevData;
              }
              
              console.log('Adding new notification with ActiveOrderId in useOrderNotificationSSE:', dataWithActiveOrderId);
              
              // 新しい通知を追加する（オブジェクトとして追加）
              const newData = [...prevData, dataWithActiveOrderId];
                            
              return newData;
            });

            // サイレント種別は orderNotificationData への追加（→ 親の再取得）だけ行い、ベル・履歴・音は更新しない
            if (isSilent) {
              return;
            }

            const message = (() => {
              if (data.notificationType === 'clerkCall') {
                return `${diningTableName}から店員呼び出しがありました。`;
              } else if (data.notificationType === 'cartCheckout') {
                return `${diningTableName}から新規注文があります。`;
              } else if (data.notificationType === 'checkoutRequest') {
                // 顧客が選んだ支払方法があれば併記する（例:「テーブルAから会計リクエスト（現金）がありました。」）
                const method = data.paymentMethod ? `（${data.paymentMethod}）` : '';
                return `${diningTableName}から会計リクエスト${method}がありました。`;
              } else {
                return '不明な通知タイプです。';
              }
            })();

            setNotificationMessages(prevMessages => [
              { time: currentTime, message: message},
              ...prevMessages,
            ]);
            setNotificationColor('blue');
            setTimeout(() => setNotificationShake(false), 1000);

            if (audioAllowed) {
              playNotificationSound();
            }
          }
        } catch (error) {
          console.error('Error processing SSE message:', error);
        }
      };

      es.onmessage = handleMessage;

      return () => {
        if (sseRef.current) {
          sseRef.current.close();
          sseRef.current = null;
        }
      };
    } catch (error) {
      console.error('Error establishing SSE connection:', error);
    }
  }, [url, storeId, clerkId, activeStore, audioAllowed, playNotificationSound, startSSE]);

  const openNotificationDialog = () => {
    setNotificationDialogOpen(true);
  };

  const closeNotificationDialog = () => {
    setNotificationDialogOpen(false);
    setNotificationColor('inherit');
  };

  return { unlockAudio, lockAudio, audioAllowed, notificationShake, notificationMessages, notificationDialogOpen, openNotificationDialog, closeNotificationDialog, notificationColor, orderNotificationData };
};
